<template>
  <div>
    <div
      v-if="!member"
      class="relative mb-4 h-[16px] animate-pulse rounded bg-zinc-200"
    />
    <div v-else class="flex gap-3">
      <div
        class="mb-4 font-Aeonik font-bold uppercase leading-none tracking-tight"
      >
        {{ member.firstName }} {{ member.lastName }}
      </div>
      <IconsSpinner
        v-if="isLoading"
        class="spinner-animation absolute right-1 top-1 h-10 w-10"
      />
    </div>

    <div class="flex flex-col gap-3">
      <div class="flex items-center gap-3">
        <Box>
          <ProfileImpactImage size="medium" :type="Campaign.TFTF" colored />
        </Box>
        <div class="flex flex-col gap-1">
          <div
            v-if="isLoading"
            class="h-[22px] animate-pulse rounded bg-zinc-200"
          />
          <span
            v-else-if="metrics"
            class="font-Kontesa text-[22px] font-bold leading-none tracking-tighter"
          >
            {{ numberFormatter.format(metrics.trees) }}
          </span>
          <span class="font-Aeonik text-[14px] leading-[16px] tracking-tighter">
            Trees planted
          </span>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <Box>
          <ProfileImpactImage size="medium" :type="Campaign.PF" colored />
        </Box>
        <div class="flex flex-col gap-1">
          <div
            v-if="isLoading"
            class="h-[22px] animate-pulse rounded bg-zinc-200"
          />
          <span
            v-else-if="metrics"
            class="font-Kontesa text-[22px] font-bold leading-none tracking-tighter"
          >
            {{ numberFormatter.format(metrics.ocean) }}
          </span>
          <span class="font-Aeonik text-[14px] leading-[16px] tracking-tighter">
            Lbs of plastic removed
          </span>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <Box>
          <ProfileImpactImage size="medium" :type="Campaign.SARTHAK" colored />
        </Box>
        <div class="flex flex-col gap-1">
          <div
            v-if="isLoading"
            class="h-[22px] animate-pulse rounded bg-zinc-200"
          />
          <span
            v-else-if="metrics"
            class="font-Kontesa text-[22px] font-bold leading-none tracking-tighter"
          >
            {{ numberFormatter.format(metrics.education) }}
          </span>
          <span class="font-Aeonik text-[14px] leading-[16px] tracking-tighter">
            Days of education funded
          </span>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <Box>
          <ProfileImpactImage size="medium" :type="Campaign.REEFGEN" colored />
        </Box>
        <div class="flex flex-col gap-1">
          <div
            v-if="isLoading"
            class="h-[22px] animate-pulse rounded bg-zinc-200"
          />
          <span
            v-else-if="metrics"
            class="font-Kontesa text-[22px] font-bold leading-none tracking-tighter"
          >
            {{ numberFormatter.format(metrics.seagrass) }}
          </span>
          <span class="font-Aeonik text-[14px] leading-[16px] tracking-tighter">
            Seagrass seeds planted
          </span>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <Box>
          <ProfileImpactImage size="medium" type="family" colored />
        </Box>
        <div class="flex flex-col gap-1">
          <div
            v-if="isLoading"
            class="h-[22px] animate-pulse rounded bg-zinc-200"
          />
          <span
            v-else-if="metrics"
            class="font-Kontesa text-[22px] font-bold leading-none tracking-tighter"
          >
            {{ numberFormatter.format(metrics.family) }}
          </span>
          <span class="font-Aeonik text-[14px] leading-[16px] tracking-tighter">
            Members
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useQuery } from "@tanstack/vue-query";
import { memberQueries } from "~/domains/member/queries";
import { Campaign } from "~/lib/campaigns";

const props = defineProps<{
  member?: { id: number; firstName: string; lastName: string };
}>();

const { data, isLoading } = useQuery({
  ...memberQueries.asGuest(props.member!.id),
  enabled: !!props.member,
});

const metrics = computed(() =>
  data.value?.metrics.reduce(
    (acc, item) => {
      switch (item.campaignId) {
        case Campaign.TFTF:
          acc.trees += item.points;
          return acc;
        case Campaign.OVI:
        case Campaign.PF:
        case Campaign.CLEANHUB:
          acc.ocean += item.points;
          return acc;
        case Campaign.SARTHAK:
          acc.education += item.points;
          return acc;
        case Campaign.REDWOOD:
          return acc;
        case Campaign.REEFGEN:
          acc.seagrass += item.points;
          return acc;
      }
    },
    {
      ocean: 0,
      education: 0,
      trees: 0,
      seagrass: 0,
      family: data.value.referrals,
      impact: data.value.points,
    },
  ),
);

const numberFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  notation: "compact",
  maximumFractionDigits: 2,
});
</script>

<style>
.spinner-animation {
  animation: rotating 1.2s linear infinite;
}
@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
